import type { FormDataModel } from './form-data-model.ts';
import type { FormValidationMessages } from './form-validation-messages.ts';

type FormScreenStateListener = () => void;

/**
 * Holds in-memory form field values for the active form screen.
 */
export class FormScreenStateStore {
  private data: Record<string, string> = {};
  private readonly listeners = new Set<FormScreenStateListener>();

  /**
   * Returns a shallow copy of all field values.
   *
   * @returns Current form state keyed by field id.
   */
  getData(): Record<string, string> {
    return { ...this.data };
  }

  /**
   * Returns a single field value.
   *
   * @param key - Field id.
   * @returns Stored value when present.
   */
  getValue(key: string): string | undefined {
    return this.data[key];
  }

  /**
   * Stores a field value and notifies subscribers.
   *
   * @param key - Field id.
   * @param value - Serialized field value.
   */
  setValue(key: string, value: string): void {
    this.data[key] = value;
    this.notify();
  }

  /**
   * Replaces all field values at once.
   *
   * @param data - New form state keyed by field id.
   */
  setData(data: Record<string, string>): void {
    this.data = { ...data };
    this.notify();
  }

  /**
   * Subscribes to form state changes.
   *
   * @param listener - Callback invoked when state changes.
   * @returns Unsubscribe function.
   */
  subscribe(listener: FormScreenStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }
}

export type FormScreenRuntime = {
  screenId: string;
  state: FormScreenStateStore;
  formDataModel: FormDataModel | null;
  validationMessages?: FormValidationMessages;
};

let activeRuntime: FormScreenRuntime | null = null;

/**
 * Sets the active form screen runtime for actions and placeholder inflation.
 *
 * @param runtime - Active runtime, or null to clear.
 */
export function setFormScreenRuntime(runtime: FormScreenRuntime | null): void {
  activeRuntime = runtime;
}

/**
 * Returns the active form screen runtime when available.
 *
 * @returns Active runtime or null.
 */
export function getFormScreenRuntime(): FormScreenRuntime | null {
  return activeRuntime;
}
